import React, { useState, useRef } from 'react'
import { Input, Button, ConfigProvider } from 'antd'
import classnames from 'classnames'
import SparkleIndicator from '../SparkleIndicator'
import type { Action, ActionPayload } from '../../../models/aiAnalysis/workflow'
import type { EditedPayload } from './types'
import styles from './styles.module.scss'

const { TextArea } = Input

/**
 * AI draft editing component for approve/decline workflow.
 * Supports text editing with approve/decline actions.
 *
 * IMPORTANT: Parent components should use key={`${action.id}-${action.action_type}`}
 * to ensure the component remounts when switching between different actions or action types.
 */
export interface AiDraftEditorProps {
  action: Action
  onApprove: (actionId: string, decision: 'approved' | 'edited', editedPayload: EditedPayload | null) => Promise<void>
  onDecline: () => Promise<void>
  currentText: string
  onTextChange: (text: string) => void
}

const getRecipientType = (payload: ActionPayload): string => {
  if (payload.shared_with_agent) return 'Notary'
  if (payload.shared_with_client) return 'Client'
  if (payload.shared_with_consumer) return 'Consumer'
  if (payload.shared_with_admin) return 'Scheduler'
  return 'Recipient'
}

const CHARACTER_LIMIT = 1500
const SHOW_COUNT_THRESHOLD = 1200
const CHAR_COUNT_ID = 'ai-draft-char-count'

const AiDraftEditor: React.FC<AiDraftEditorProps> = ({ action, onApprove, onDecline, currentText, onTextChange }) => {
  const isSecondMessage = action.action_type === 'acknowledge'

  const recipientType = getRecipientType(action.action_payload)

  const [submittingAction, setSubmittingAction] = useState<'approve' | 'decline' | null>(null)

  const originalTextRef = useRef<string>(action.action_payload.text)
  const isEdited = currentText !== originalTextRef.current

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onTextChange(e.target.value)
  }

  const handleApprove = async () => {
    setSubmittingAction('approve')
    try {
      if (isEdited) {
        await onApprove(action.id, 'edited', { text: currentText })
      } else {
        await onApprove(action.id, 'approved', null)
      }
    } finally {
      setSubmittingAction(null)
    }
  }

  const handleDecline = async () => {
    setSubmittingAction('decline')
    try {
      await onDecline()
    } finally {
      setSubmittingAction(null)
    }
  }

  const isSubmitting = submittingAction !== null
  const isApproveDisabled = isSubmitting || !currentText.trim()
  const shouldShowCount = currentText.length > SHOW_COUNT_THRESHOLD

  return (
    <div className={styles.container} data-second-message={isSecondMessage}>
      <div className={styles.recipientInfo}>
        <SparkleIndicator aiGenerated={true} />
        <span className={styles.recipientText} data-test="recipient-label">
          {isSecondMessage ? 'Respond to' : 'Comment to'} {recipientType.toLowerCase()}
        </span>
      </div>

      <div className={styles.textareaWrapper}>
        {/* ConfigProvider overrides the default Ant Design theme tokens for this component only.
          We set borders to transparent and remove shadows so we can control them purely
          via CSS classes.
        */}
        <ConfigProvider
          theme={{
            components: {
              Input: {
                colorBorder: 'transparent',
                hoverBorderColor: 'transparent',
                activeBorderColor: 'transparent',
                activeShadow: 'none',
                boxShadow: 'none'
              }
            }
          }}
        >
          <TextArea
            className={classnames(styles.textarea, {
              [styles.textareaSecondMessage]: isSecondMessage
            })}
            value={currentText}
            onChange={handleTextChange}
            autoSize={{ minRows: 3, maxRows: 3 }}
            maxLength={CHARACTER_LIMIT}
            showCount={shouldShowCount}
            disabled={isSubmitting}
            aria-label="AI-generated comment draft"
            aria-describedby={CHAR_COUNT_ID}
          />
        </ConfigProvider>
        <span
          id={CHAR_COUNT_ID}
          style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}
        >
          Character limit: {CHARACTER_LIMIT}. Current count: {currentText.length}
        </span>
      </div>

      <div className={styles.actions}>
        <Button
          className={styles.cancelButton}
          onClick={handleDecline}
          disabled={isSubmitting}
          loading={submittingAction === 'decline'}
          aria-label={`Cancel AI-generated comment to ${recipientType.toLowerCase()}`}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          className={styles.createButton}
          onClick={handleApprove}
          disabled={isApproveDisabled}
          loading={submittingAction === 'approve'}
          aria-label={`Send comment to ${recipientType.toLowerCase()}`}
        >
          Send
        </Button>
      </div>
    </div>
  )
}

export default AiDraftEditor
